import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {supplementalType, SupplementalType, toSupplementalType} from "./supplemental-type";
import {Default} from "../util/decorators";

export const ObjectPictureAssocValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    supplementalType: t.string,
    supplementalGroup: t.string,
    url: t.string,
  }),
  t.partial({
    description: t.string,
    fileName: t.string,
    personId: t.string,
    personName: t.string,
    reportable: t.boolean,
    rotation: t.number,
    latCoord: t.number,
    longCoord: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ObjectPictureAssocArrayValidator = t.array(ObjectPictureAssocValidator);

export type ObjectPictureAssoc = t.TypeOf<typeof ObjectPictureAssocValidator>;
export type ObjectPictureAssocArray = t.TypeOf<typeof ObjectPictureAssocArrayValidator>;

@Exclude()
export class ObjectPictureAssocEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toSupplementalType))
  @Default(supplementalType.other)
  supplementalType!: SupplementalType;

  @Expose()
  supplementalGroup!: string

  @Expose()
  url!: string;

  @Expose()
  description?: string

  @Expose()
  fileName?: string

  @Expose()
  personId?: string;

  @Expose()
  personName?: string

  @Expose()
  reportable?: boolean

  @Expose()
  rotation?: number

  @Expose()
  latCoord?: number

  @Expose()
  longCoord?: number

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'OBJECTPICASSOC';

  get displayName(): Readonly<string> {
    let name = ""

    if (this.fileName) {
      name += this.fileName
      name += " - "
    }

    name += (this.description ? this.description : "No Description")

    return name;
  }

  /**
   * Constructor for ObjectPictureAssocEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: ObjectPictureAssoc): ObjectPictureAssocEntity {
    return plainToInstance(ObjectPictureAssocEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<ObjectPictureAssoc> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ObjectPictureAssocValidator, object);
  }

  asTypeValue(): ObjectPictureAssoc {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(ObjectPictureAssocValidator, object);
  }
}

export const ObjectPictureUploadDTOValidator = t.intersection([
  t.type({
    objectId: t.string,
    objectType: t.string,
  }),
  t.partial({
    description: t.string,
    fileName: t.string,
    personId: t.string,
    reportable: t.boolean,
    rotation: t.number,
    supplementalType: t.string,
  }),
]);

export type ObjectPictureAssocUploadDTO = t.TypeOf<typeof ObjectPictureUploadDTOValidator>;

export type ObjectPictureAssocUpload = ObjectPictureAssocUploadDTO & {
  file: File
}
